import { getCoreRowModel, useReactTable } from "@tanstack/react-table";
import type {
  ColumnDef,
  PaginationState,
  SortingState,
  Updater,
} from "@tanstack/react-table";

import { ServicePagination } from "~/api";
import { Table } from "~/ui/table/Table";
import { tw } from "~/utils";
import { PaginationControls } from "../PaginationControls";

export type FormErrorType = string | boolean;

export interface PaginatedTableProps<TData> {
  data: TData[] | undefined;
  pagination: ServicePagination | undefined;
  columns: ColumnDef<TData>[];
  setCurrentPage: (p: number) => void;
  sorting: SortingState;
  setSorting: (s: SortingState) => void;
  isLoadingData: boolean;
  onRowClick?: (row: TData) => void;
}

export const PaginatedTable = <TData,>({
  data,
  columns,
  setCurrentPage,
  sorting,
  setSorting,
  isLoadingData,
  pagination,
  onRowClick,
}: PaginatedTableProps<TData>) => {
  const totalPages = pagination?.totalPages ?? 0;
  const currentPage = pagination?.currentPage ?? 1;
  const pageSize = pagination?.perPage ?? 0;
  const itemsCount = pagination?.count ?? 0;

  const handlePaginationChange = (updater: Updater<PaginationState>) => {
    if (updater instanceof Function) {
      const params = updater({ pageIndex: currentPage - 1, pageSize });
      setCurrentPage(params.pageIndex + 1);
    }
  };

  const handleSortChange = (updater: Updater<SortingState>) => {
    if (updater instanceof Function) {
      const params = updater(sorting);
      setSorting(params);
    }
  };

  const table = useReactTable({
    data: data ?? [],
    columns,
    manualPagination: true,
    manualSorting: true,
    enableSortingRemoval: true,
    pageCount: totalPages,
    state: {
      pagination: { pageIndex: currentPage - 1, pageSize },
      sorting,
    },
    getCoreRowModel: getCoreRowModel(),
    onPaginationChange: handlePaginationChange,
    onSortingChange: handleSortChange,
  });

  return (
    <>
      <Table
        isLoadingData={isLoadingData}
        table={table}
        rowsPerPage={pageSize}
        onRowClick={onRowClick}
        className={tw(
          "mt-1 rounded-b-none border-0",
          !pagination?.totalPages ? "sm:rounded-t-lg" : "sm:rounded-lg",
        )}
      />
      {!!totalPages && (
        <PaginationControls
          currentPage={currentPage}
          numPages={totalPages}
          setPage={(i: number) => table.setPageIndex(i - 1)}
          itemsCount={itemsCount}
          className="flex w-full items-center gap-2 bg-white px-4 py-3"
        />
      )}
    </>
  );
};
PaginatedTable.displayName = "PaginatedTable";
